import { GoogleAuthProvider, linkWithPopup, getAuth } from 'firebase/auth';
import axiosInstance from '../../lib/api/axiosInstance';

/**
 * Lie un compte Google au compte Firebase actuel
 */
export const linkGoogleAccount = async (): Promise<boolean> => {
  const auth = getAuth();
  const currentFirebaseUser = auth.currentUser;
  
  // Vérifier que l'utilisateur est connecté
  if (!currentFirebaseUser) {
    throw new Error('Vous devez être connecté pour lier votre compte Google');
  }
  
  try {
    // Créer le fournisseur Google avec les scopes requis
    const provider = new GoogleAuthProvider();
    
    // Ajouter les scopes pour le calendrier
    provider.addScope('https://www.googleapis.com/auth/calendar.readonly');
    provider.addScope('https://www.googleapis.com/auth/calendar.events');
    
    // Ajouter les scopes pour Gmail
    provider.addScope('https://www.googleapis.com/auth/gmail.readonly');
    provider.addScope('https://www.googleapis.com/auth/gmail.modify');
    provider.addScope('https://www.googleapis.com/auth/gmail.labels');
    
    // Force le re-consentement pour s'assurer que les scopes sont bien demandés
    provider.setCustomParameters({
      prompt: 'consent'
    });
    
    // Lier le compte Google à l'utilisateur actuel
    const result = await linkWithPopup(currentFirebaseUser, provider);
    const credential = GoogleAuthProvider.credentialFromResult(result);
    
    if (!credential || !credential.accessToken) {
      console.error("Aucun token d'accès reçu après liaison");
      return false;
    }
    
    // Enregistrer les informations du token sur le backend
    try {
      await saveGoogleTokenToBackend(credential.accessToken);
      return true;
    } catch (saveError) {
      console.error("Erreur lors de l'enregistrement du token:", saveError);
      throw new Error("Échec de l'enregistrement du token après la liaison réussie");
    }
  } catch (error: any) {
    console.error("Erreur lors de la liaison avec Google:", error);
    
    // Si l'utilisateur est déjà authentifié avec cette méthode
    if (error.code === 'auth/credential-already-in-use') {
      try {
        // Essayer d'extraire le credential du résultat d'erreur
        const credential = GoogleAuthProvider.credentialFromError(error);
        
        if (credential && credential.accessToken) {
          console.log("Credential already in use, but token is available");
          // Tenter quand même d'enregistrer le token
          try {
            await saveGoogleTokenToBackend(credential.accessToken);
            console.log("Successfully saved token from existing credential");
            return true;
          } catch (tokenError) {
            console.error("Failed to save token from existing credential:", tokenError);
            // Créer une erreur plus informative
            throw new Error("Problème de communication avec le serveur lors de l'enregistrement du token");
          }
        } else {
          // Pas d'accessToken disponible dans l'erreur
          console.warn("Credential already in use but no access token available");
          // On considère que la liaison est réussie même si on n'a pas pu enregistrer le token
          return true;
        }
      } catch (credentialError) {
        console.error("Error extracting credential from error:", credentialError);
        // On considère que la liaison est réussie
        return true;
      }
    }
    
    // Autres erreurs
    throw error;
  }
};

/**
 * Vérifie si le compte utilisateur est lié à un compte Google
 */
export const isGoogleAccountLinked = async (): Promise<boolean> => {
  try {
    // Retirer le préfixe /api car il est déjà dans baseURL
    const response = await axiosInstance.get<{ linked: boolean }>('/google/status');
    
    return response.data.linked === true;
  } catch (error) {
    console.error("Erreur lors de la vérification de la liaison Google:", error);
    return false;
  }
};

/**
 * Enregistre le token Google sur le backend
 */
const saveGoogleTokenToBackend = async (accessToken: string): Promise<void> => {
  try {
    console.log("Attempting to save token to backend");
    // URL correcte: /google/save-token (sans le préfixe /api puisqu'il est déjà dans la baseURL)
    const response = await axiosInstance.post<{ success: boolean }>('/google/save-token', {
      accessToken
    });
    
    console.log("Save token response:", response.status, response.statusText);
    
    if (!response.data.success) {
      throw new Error("Le serveur a retourné un statut d'erreur");
    }
    
    console.log("Token successfully saved to backend");
  } catch (error: any) {
    console.error("Erreur lors de l'enregistrement du token:", error);
    
    if (error.request && error.request.responseURL) {
      console.log("Request was made to URL:", error.request.responseURL);
    }
    
    // Améliorer le message d'erreur avec plus de détails
    if (error.response) {
      console.error("Server response:", error.response.data);
      throw new Error(`Erreur serveur: ${error.response.status} - ${error.response.data.error || 'Erreur inconnue'}`);
    }
    
    throw error;
  }
};
